import { useState } from 'react';
import { Box, Button, Chip, IconButton, InputBase, MenuItem, Popover, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { isSameParty } from '../../../lib/packingListNormalize';
import { headerMaxLength } from '../../../lib/packingListFields';
import { REVIEW_TABLE_HEAD_BG } from '../../../lib/packingListReviewStyles';
import { headerConfidence } from '../../../lib/packingListWire';
import ConfidenceMark from './ConfidenceMark';

const ROW_DIVIDER = '1px solid #edf0f2';
const DIFFERS_COLOR = '#b5651d';
/** 바깥 테두리만 둥글게 두고, 칸 사이 세로 경계선 없이 줄 사이만 옅게 나눈다. */
const tableSx = {
    tableLayout: 'fixed',
    '& .MuiTableCell-root': { px: 1.5, py: 0, height: 40, fontSize: 12, borderBottom: ROW_DIVIDER },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
    '& .MuiTableCell-head': { bgcolor: REVIEW_TABLE_HEAD_BG, color: 'text.secondary', fontWeight: 500, height: 36 },
    '& .MuiInputBase-root': { width: '100%', fontSize: 12 },
    '& .MuiInputBase-input': { p: 0 },
};

/**
 * PL 기재값과 House 거래처를 견준 결과. House 거래처 이름과 같거나 그 거래처에 등록된 별칭과 같으면 일치, 그 밖은 모두 다름이다.
 * PL 값이 비어 있으면 견줄 것이 없다.
 */
const compareParty = (houseName, plValue, aliases) => {
    if (!plValue) return null;
    return houseName && isSameParty(houseName, plValue, aliases) ? 'match' : 'mismatch';
};

const RESULT_CHIP = {
    match: { label: 'Matches', Icon: CheckRoundedIcon, color: '#2e7d32', bgcolor: '#e6f4ea' },
    aliasSaved: { label: 'Alias saved', Icon: BookmarkAddedRoundedIcon, color: '#1565c0', bgcolor: '#e3f0fc' },
    aliasApplied: { label: 'Alias applied', Icon: CheckRoundedIcon, color: '#1565c0', bgcolor: '#e3f0fc' },
    mismatch: { label: 'Differs', Icon: WarningAmberRoundedIcon, color: DIFFERS_COLOR, bgcolor: '#fdecd8' },
};

function ResultChip({ result }) {
    const { label, Icon, color, bgcolor } = RESULT_CHIP[result];
    return (
        <Chip
            size="small"
            label={label}
            icon={<Icon />}
            sx={{ height: 22, fontSize: 12, fontWeight: 600, color, bgcolor, '& .MuiChip-label': { px: 1 }, '& .MuiChip-icon': { fontSize: 14, color: 'inherit', ml: 0.75, mr: -0.5 } }}
        />
    );
}

/** 확인 창 안의 라벨·값 한 줄. 두 값 모두 읽기 전용이다. */
function ReviewValueRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 30 }}>
            <Typography sx={{ width: 56, flexShrink: 0, fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, wordBreak: 'break-word' }}>{value || '—'}</Typography>
        </Box>
    );
}

const NAVY = '#003a52';

/**
 * 다른 표기를 확인하는 창. House 값은 그대로 둔다.
 * Yes면 PL 표기를 House 거래처의 별칭으로 등록하고, No면 같은 거래처에 이미 등록된 다른 별칭 중 하나를 골라 Confirm할 때 On PL 값을 바꾼다.
 */
function ReviewPopover({ anchorEl, onClose, houseName, plValue, aliases, onRegister, onPickAlias }) {
    const [choosing, setChoosing] = useState(false);
    const [picked, setPicked] = useState('');

    const backToQuestion = () => {
        setChoosing(false);
        setPicked('');
    };

    return (
        <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose} TransitionProps={{ onExited: backToQuestion }} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} slotProps={{ paper: { sx: { p: 2, width: 380, borderRadius: '10px' } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Save the PL name as an alias?</Typography>
                <IconButton size="small" onClick={onClose} title="Close" sx={{ p: '2px' }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            <Box sx={{ bgcolor: REVIEW_TABLE_HEAD_BG, borderRadius: '6px', px: 1.5, py: 1, mb: 1.5 }}>
                <ReviewValueRow label="Master" value={houseName} />
                <ReviewValueRow label="On PL" value={plValue} />
            </Box>
            {choosing ? (
                <>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>Choose one of the aliases already saved for this party</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={picked}
                            onChange={(e) => setPicked(e.target.value)}
                            disabled={aliases.length === 0}
                            SelectProps={{ displayEmpty: true, renderValue: (value) => value || (aliases.length === 0 ? 'No aliases registered for this party' : 'Select an alias…') }}
                            sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
                        >
                            {aliases.map((alias) => (
                                <MenuItem key={alias} value={alias} sx={{ fontSize: 12 }}>{alias}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button size="small" onClick={backToQuestion} sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>Back</Button>
                        <Button size="small" variant="contained" disableElevation disabled={!picked} onClick={() => onPickAlias(picked)} sx={{ fontSize: 12, fontWeight: 600, bgcolor: NAVY, '&:hover': { bgcolor: '#00283a' } }}>Confirm</Button>
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Button size="small" variant="contained" disableElevation onClick={onRegister} sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none', bgcolor: NAVY, '&:hover': { bgcolor: '#00283a' } }}>Yes, save as alias</Button>
                        <Button size="small" variant="outlined" onClick={() => setChoosing(true)} sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none', color: 'text.primary', borderColor: '#cfd6db', '&:hover': { borderColor: '#aab4bb', bgcolor: 'white' } }}>No, use a saved alias</Button>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Yes keeps the master name and saves the PL name as its alias, so this spelling won't be flagged again. No replaces the PL name with an alias already saved for this party.</Typography>
                </>
            )}
        </Popover>
    );
}

function PartyRow({ field, label, houseParty, plValue, aliases, onPlChange, onRegisterAlias, highlight, readOnly, confidence = null }) {
    const [reviewAnchor, setReviewAnchor] = useState(null);
    /** 별칭으로 바꾼 직전 값. 그 뒤 On PL을 직접 고치면 되돌릴 대상이 아니다. */
    const [replaced, setReplaced] = useState(null);
    /** 이 행의 팝오버에서 별칭으로 등록한 표기. 등록이 끝나 일치가 되면 Matches 대신 등록했다는 표시를 낸다. */
    const [savedAlias, setSavedAlias] = useState(null);
    const houseName = houseParty?.name ?? '';
    const compared = compareParty(houseName, plValue, aliases);
    const aliasApplied = replaced != null && replaced.to === plValue;
    const result = compared !== 'match' ? compared : savedAlias != null && savedAlias === plValue ? 'aliasSaved' : aliasApplied ? 'aliasApplied' : compared;
    const mismatch = result === 'mismatch';
    const canUndo = !readOnly && aliasApplied;

    const handlePlChange = (value) => {
        setReplaced(null);
        onPlChange(value);
    };

    const actionButtonSx = { height: 26, minWidth: 0, px: 1.5, fontSize: 11, fontWeight: 700, bgcolor: 'white' };

    return (
        <TableRow sx={mismatch ? { bgcolor: '#fff8f0' } : undefined}>
            <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
            <TableCell title={houseName} sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{houseName || '—'}</TableCell>
            <TableCell sx={{ position: 'relative', ...(highlight ? { outline: '2px solid #d92936', outlineOffset: -2 } : {}) }}>
                <InputBase id={`header-${field}`} value={plValue ?? ''} onChange={(e) => handlePlChange(e.target.value)} readOnly={readOnly} placeholder="As written on PL" inputProps={{ maxLength: headerMaxLength(field), 'aria-label': `${label} on PL` }} />
                <ConfidenceMark confidence={confidence} field={field} edited={false} />
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    {result ? <ResultChip result={result} /> : <span />}
                    {canUndo ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                onPlChange(replaced.from);
                                setReplaced(null);
                            }}
                            sx={{ ...actionButtonSx, color: NAVY, borderColor: '#cfd6db', '&:hover': { borderColor: '#aab4bb', bgcolor: 'white' } }}
                        >
                            UNDO
                        </Button>
                    ) : (
                        mismatch &&
                        !readOnly &&
                        houseParty?.id != null && (
                            <Button size="small" variant="outlined" onClick={(e) => setReviewAnchor(e.currentTarget)} sx={{ ...actionButtonSx, color: DIFFERS_COLOR, borderColor: DIFFERS_COLOR, '&:hover': { borderColor: DIFFERS_COLOR, bgcolor: '#fdf3e7' } }}>
                                REVIEW
                            </Button>
                        )
                    )}
                </Box>
                <ReviewPopover
                    anchorEl={reviewAnchor}
                    onClose={() => setReviewAnchor(null)}
                    houseName={houseName}
                    plValue={plValue}
                    aliases={aliases ?? []}
                    onRegister={() => {
                        setReviewAnchor(null);
                        setSavedAlias(plValue);
                        onRegisterAlias(houseParty.id, plValue);
                    }}
                    onPickAlias={(alias) => {
                        setReviewAnchor(null);
                        onPlChange(alias);
                        setReplaced({ from: plValue ?? '', to: alias });
                    }}
                />
            </TableCell>
        </TableRow>
    );
}

/**
 * Shipper·Consignee를 한 표로 견준다. 운영값은 House의 Trade Partner(Name used)이고, PL 기재값(On PL)은 표 안에서 바로 고친다.
 * 다르면 등록되지 않은 별칭으로 보고 REVIEW에서 그 표기를 House 거래처의 별칭으로 등록하거나, 이미 등록된 별칭으로 On PL 값을 바꾼다. 어느 쪽이든 다시 일치가 된다.
 * 별칭으로 바꾼 행은 REVIEW 자리에 UNDO가 나와 바꾸기 전 값으로 되돌린다.
 * House 값 자체는 여기서 고치지 않는다.
 * readOnly면 PL 기재값을 고치지 못하고 REVIEW도 없다.
 */
export default function PartyCompareTable({ header, onChange, houseShipper, houseConsignee, partyAliases, onRegisterAlias, highlightField, readOnly = false }) {
    const rows = [
        { field: 'shipper_name', label: 'Shipper', houseParty: houseShipper },
        { field: 'consignee_name', label: 'Consignee', houseParty: houseConsignee },
    ];

    return (
        <Box sx={{ border: '1px solid #e0e4e7', borderRadius: '8px', overflow: 'hidden' }}>
            <Table size="small" sx={tableSx}>
                <colgroup>
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '24%' }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell>Party</TableCell>
                        <TableCell>Name used (Master)</TableCell>
                        <TableCell>On PL</TableCell>
                        <TableCell>Result</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <PartyRow
                            key={row.field}
                            {...row}
                            plValue={header[row.field]}
                            confidence={headerConfidence(header, row.field)}
                            aliases={partyAliases[row.houseParty?.id]}
                            onPlChange={(v) => onChange(row.field, v)}
                            onRegisterAlias={onRegisterAlias}
                            highlight={highlightField === row.field}
                            readOnly={readOnly}
                        />
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}
